// src/features/editor/components/blocks/MatchBlockEditor.tsx
import { Plus, Trash2 } from 'lucide-react'
import type { MatchBlock, MatchPair } from '@/types/blocks'
import { Button, Label } from '@teach/ui'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useStableKeys } from '@/features/editor/hooks/useStableKeys'
import { getLocalizedField, setLocalizedField } from '@/features/editor/lib/localizedField'
import { MATCH_LIMITS } from '@/features/editor/constants/fieldLimits'
import { charsOk } from '@/features/editor/lib/quizWordLimits'
import { AutoGrowTextField } from './shared/AutoGrowTextField'
import { CharCount } from './shared/QuizFieldCounters'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: MatchBlock
  onChange: (next: MatchBlock) => void
}

export const MatchBlockEditor = ({ block, onChange }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const contentLang = useContentLang()
  const { keys, removeAt } = useStableKeys(block.pairs.length, 'pair')

  const setPair = (index: number, patch: Partial<MatchPair>) => {
    const next = block.pairs.map((p, i) => (i === index ? { ...p, ...patch } : p))
    onChange({ ...block, pairs: next })
  }
  const addPair = () => {
    if (block.pairs.length >= MATCH_LIMITS.PAIRS_MAX) return
    onChange({
      ...block,
      pairs: [
        ...block.pairs,
        {
          leftText: null,
          leftTextEn: null,
          rightText: null,
          rightTextEn: null,
          leftOrder: block.pairs.length,
          rightOrder: block.pairs.length,
        },
      ],
    })
  }
  const removePair = (index: number) => {
    removeAt(index)
    onChange({ ...block, pairs: block.pairs.filter((_, i) => i !== index) })
  }

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-foreground font-medium">{m.pairs}</Label>
          <span className="text-xs text-muted-foreground">· {block.pairs.length}</span>
        </div>

        <div className="flex flex-col gap-2">
          {block.pairs.map((p, i) => {
            const leftVal = getLocalizedField(p, 'leftText', contentLang)
            const rightVal = getLocalizedField(p, 'rightText', contentLang)
            return (
              <div key={keys[i]} className="flex items-start gap-2">
                <div className="min-w-0 flex-1 space-y-1">
                  <AutoGrowTextField
                    value={leftVal ?? ''}
                    placeholder={cm.leftPart}
                    maxLength={MATCH_LIMITS.PAIR_TEXT_CHARS}
                    onChange={(value) => {
                      if (!charsOk(value, MATCH_LIMITS.PAIR_TEXT_CHARS, leftVal)) return
                      setPair(i, setLocalizedField('leftText', contentLang, value))
                    }}
                  />
                  <CharCount value={leftVal} max={MATCH_LIMITS.PAIR_TEXT_CHARS} />
                </div>
                <span className="mt-2.5 shrink-0 font-bold text-primary">→</span>
                <div className="min-w-0 flex-1 space-y-1">
                  <AutoGrowTextField
                    value={rightVal ?? ''}
                    placeholder={cm.rightPart}
                    maxLength={MATCH_LIMITS.PAIR_TEXT_CHARS}
                    onChange={(value) => {
                      if (!charsOk(value, MATCH_LIMITS.PAIR_TEXT_CHARS, rightVal)) return
                      setPair(i, setLocalizedField('rightText', contentLang, value))
                    }}
                  />
                  <CharCount value={rightVal} max={MATCH_LIMITS.PAIR_TEXT_CHARS} />
                </div>
                {/* На минимуме пар корзина неактивна, а не пропадает: строка не прыгает. */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removePair(i)}
                  disabled={block.pairs.length <= MATCH_LIMITS.PAIRS_MIN}
                  aria-label={m.a11yRemove}
                  className="mt-1 h-8 w-8 shrink-0 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )
          })}

          {block.pairs.length < MATCH_LIMITS.PAIRS_MAX ? (
            <Button variant="secondary" size="sm" onClick={addPair}>
              <Plus className="mr-1.5 h-4 w-4" />
              {m.addPair}
            </Button>
          ) : null}
        </div>
      </section>
    </div>
  )
}
